import { Injectable } from '@angular/core';
import {
  ActivityStatusEnum,
  ActivityTypeEnum,
  PaginatedResponseInterface,
  RangeQueryInterface,
  SortEnum,
  TimeslotFilterParamsInterface,
  TimeslotStatusEnum,
} from '@sport-activities/sdk';
import { format } from 'date-fns';
import { Observable, of } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';

import { EventsByDate } from '~/attendee/interfaces/event.interface';
import { Activity } from '~/core/model/activity.model';
import { Timeslot } from '~/core/model/timeslot.model';
import { ActivityService } from '~/core/services/activity/activity.service';
import { LocaleService } from '~/i18n/services/locale.service';
import { ToggleService } from '~/toggles/services/toggle.service';
import { ToggleNameEnum } from '~/toggles/services/toggle/enums/name.enum';
import { ActivityFilterParamsInterface } from '../interfaces/activity-filter-params.interface';
import { ActivitySectionCms } from '~/cms/interfaces/homepage.cms.interface';

@Injectable({
  providedIn: 'root',
})
export class SubscriptionSearchService {
  constructor(
    private readonly activityService: ActivityService,
    private readonly toggleService: ToggleService,
    private readonly localeService: LocaleService
  ) {}

  private get defaultFilters(): ActivityFilterParamsInterface {
    return {
      status: 'PUBLISHED' as ActivityStatusEnum,
      timeslots: {
        startDate: new Date().toISOString(),
        excludeFull: true,
        timeslotStatus: 'PUBLISHED' as TimeslotStatusEnum,
      },
      sort: {
        by: 'timeslots.startDate',
        order: 'asc' as SortEnum,
      },
    };
  }

  private getCalendarFilters(): ActivityFilterParamsInterface {
    const filters = this.defaultFilters;

    filters.pagination = {
      from: 0,
      limit: 1000,
    };

    return filters;
  }

  private getGroupFilter(akeneoGroupId?: number): [number] {
    return akeneoGroupId ? [akeneoGroupId] : undefined;
  }

  public getDynamicPageActivitiesFilters(
    section: ActivitySectionCms,
    activityType: ActivityTypeEnum[],
    tenant?: string,
    ageRange?: RangeQueryInterface<number>,
    priceRange?: RangeQueryInterface<number>,
    geo?: {
      distance?: number;
      latitude?: number;
      longitude?: number;
    }
  ): ActivityFilterParamsInterface {
    const filters = { ...this.defaultFilters };
    filters.sportGroups = section.sport_group
      ? [section.sport_group]
      : undefined;
    filters.sports = section.sport ? [section.sport] : undefined;
    filters.timeslots.tenant = tenant ? [tenant] : undefined;
    filters.timeslots.activityType = activityType;
    filters.organizationIdentifier = section.organization || undefined;
    filters.ageRange = ageRange;
    filters.timeslots.priceRange = priceRange;
    filters.geo = geo;
    filters.timeslots.startDate =
      section.date_range?.from || new Date().toISOString();
    filters.timeslots.maxStartDate = section.date_range?.to || undefined;
    filters.timeslots.excludeFull =
      !this.toggleService.isEnabled(ToggleNameEnum.ShowFullActivities) ||
      undefined;

    return filters;
  }

  public getItemsForCalendarView(
    activityPaginatedResponse: PaginatedResponseInterface<Activity>,
    filters: ActivityFilterParamsInterface
  ): Observable<{ eventsByDate: EventsByDate[]; total: number }> {
    return of(activityPaginatedResponse).pipe(
      map((results) => ({ results })),
      // get timeslots for activities
      mergeMap(({ results }) => {
        const { items, total } = results;

        if (!total) {
          return of({events: [], total: 0});
        }

        return this.activityService
          .getTimeslots({
            activityIdentifier: items.map((item) => Number(item.sku)),
            pagination: filters.pagination,
            ...filters.timeslots,
          })
          .pipe(
            map((response) => ({
              events: this.mapTimeslots(response.items, items),
              total: response.total
            }))
          );
      }),
      map(({events, total}) => {
        // group the events by date
        const sortedEvents = events.reduce((acc, event) => {
          // Format UTC date as user's local date (with good timezone) before sorting
          // so timeslot is pushed into correct date array
          const date = format(
            new Date(event.timeslot.startDate),
            'yyyy-MM-dd',
            {
              locale: this.localeService.dateLocale,
            }
          );

          if (!acc[date]) {
            acc[date] = [];
          }

          acc[date].push(event);
          return acc;
        }, {});

        return {
          total,
          eventsByDate: Object.keys(sortedEvents)
            .map((date) => {
              // sort timeslots
              sortedEvents[date] = sortedEvents[date].sort((a, b) =>
                a.timeslot.startDate >= b.timeslot.startDate ? 1 : -1
              );

              // return data
              return {
                date,
                events: sortedEvents[date],
              };
            })
            .sort((a, b) => {
              // sort activities
              return new Date(a.date).getTime() - new Date(b.date).getTime();
            }),
        };
      })
    );
  }

  private mapTimeslots(
    timeslots,
    items
  ): { activity: Activity; timeslots: Timeslot[] }[] {
    return timeslots.map((timeslot) => ({
      activity: new Activity(
        items.find((item) => item.sku === +timeslot.activityIdentifier)
      ),
      timeslot: new Timeslot(timeslot),
    }));
  }

  public getLiveFilters(tenant?: string): ActivityFilterParamsInterface {
    const filters = this.getCalendarFilters();
    filters.timeslots.activityType = ['LIVE' as ActivityTypeEnum];
    filters.timeslots.tenant = tenant ? [tenant] : undefined;

    return filters;
  }

  public getOfferPageActivitiesFilters(
    akeneoGroupId?: number,
    priceRange?: RangeQueryInterface<number>
  ): ActivityFilterParamsInterface {
    const filters = { ...this.defaultFilters };
    filters.timeslots.groups = this.getGroupFilter(akeneoGroupId);
    filters.timeslots.excludeFull =
      !this.toggleService.isEnabled(ToggleNameEnum.ShowFullActivities) ||
      undefined;
    filters.timeslots.priceRange = priceRange;

    return filters;
  }

  public getOfferPageCalendarFilters(
    akeneoGroupId?: number,
    priceRange?: RangeQueryInterface<number>
  ): ActivityFilterParamsInterface {
    const filters = this.getCalendarFilters();
    filters.timeslots.groups = filters.timeslots.groups =
      this.getGroupFilter(akeneoGroupId);
    filters.timeslots.priceRange = priceRange;

    return filters;
  }

  public getTimeslotsFilters(): TimeslotFilterParamsInterface {
    return {
      timeslotStatus: 'PUBLISHED' as TimeslotStatusEnum,
      excludeFull: false,
      startDate: new Date().toISOString(),
      sort: {
        by: 'startDate',
        order: 'asc' as SortEnum,
      },
    };
  }
}
